/**
 * RelatedProductGroupsView — the Related Products tab.
 *
 * "Related products" in this app means what the item catalog means by it: the
 * several VENDOR PRODUCTS that buy one inventory item. Roma Tomatoes from
 * Restaurant Depot and Tomatoes, Roma from Sysco are one item with two ways to
 * buy it, and that grouping is what an import builds and what the per-item
 * editor (PurchaseOptionsEditor) maintains.
 *
 * The tab used to render `item_relationships` instead — an AI-built graph of
 * SUBSTITUTES between different items (alternatives, same family). That is a
 * real feature, but it is a different question, it is generated rather than
 * confirmed by the import, and showing it here meant the tab never matched the
 * catalog it was supposed to be an editor for. buildRelatedProductCatalogGroups
 * always modelled the right thing — its own docstring says these are "the
 * groups the Related Products tab actually exists to show" — it simply had no
 * renderer. This is that renderer. Substitutes move behind their own link.
 *
 * Editing is GROUP editing (RelatedProductGroupEditor). Edit used to hand the
 * host `group.item` and open the per-item Vendor Options modal, which is the
 * editor for ONE member of a card that routinely spans several linked items
 * and several vendors — a control that promised the group and delivered a
 * fragment. The group editor adds and removes ways to buy, non-destructively,
 * and re-runs the server's group comparison after each change. Editing one
 * purchasing option is still the Vendor Options modal, reached from that
 * vendor's own row inside the editor, so there is still exactly one place
 * where a vendor product's price, pack, or item assignment can change.
 *
 * How to tell at a glance which view you are looking at, because the two are
 * easy to confuse and the whole bug was that they wore the same name:
 *
 *   THIS view  — heading "Related products". Every card is ONE inventory item;
 *                the lettered tiles under it are the vendors that sell it, and
 *                the item's own name never appears among them. A card reads
 *                "Roma Tomatoes / 2 ways to buy it", tiles "Restaurant Depot"
 *                and "Sysco". Cards carry an Edit button, no accept/dismiss.
 *
 *   Substitutes — heading "Substitutes & alternatives", one click further in.
 *                 Every card is a GROUP OF DIFFERENT ITEMS, so the lettered
 *                 tiles are other products entirely ("Beefsteak Tomatoes" next
 *                 to "Tomato Paste #10"), each card carries a relationship type
 *                 badge ("Same family", "Alternative"), and suggestions there
 *                 offer Accept / Dismiss.
 *
 * If you are seeing type badges and unrelated products side by side, you are
 * in the substitutes workspace, not here.
 *
 * Under link-don't-merge (2026-08), "one item with two ways to buy it" is
 * usually TWO inventory items joined by a confirmed same_item row. This view
 * therefore folds the per-item projection by the EQUIVALENT working set
 * (foldCatalogGroupsByEquivalence): one card per group, wearing the canonical
 * member's identity, pooling every member's vendor products, with a
 * "Canonical" pill on the canonical member's tile. The headline metrics count
 * these folded groups and their pooled products.
 */
import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import RelatedProductGroupEditor from './RelatedProductGroupEditor';
import PotentialSiblingsPanel from './PotentialSiblingsPanel';
import {
  buildRelatedProductCatalogGroups,
  foldCatalogGroupsByEquivalence,
  potentialSiblingsForGroup,
  relatedProductCatalogGroupsOnly,
  relatedProductCatalogMetrics,
} from '../utils/relatedProductCatalog';
import { buildWorkingSetGroups } from '../utils/relatedItemGroups';
import {
  buildComparablePriceDisplay,
  comparablePrecision,
  formatComparablePrice,
  roundHalfUpFixed,
} from '../utils/priceComparison';
import { buildGroupSavings, formatSavingsLine } from '../utils/groupSavings';
import { lastUsedTitle, resolveLastUsedOption } from '../utils/lastUsedOption';

const text = (value) => String(value ?? '').trim();

const styles = {
  shell: { display: 'grid', gap: 16, color: '#3D2518' },
  intro: { border: '1px solid #E8DDD4', borderRadius: 14, background: '#FAF7F4', padding: '14px 15px' },
  title: { color: '#3E2723', fontWeight: 800, fontSize: 18, margin: 0 },
  hint: { color: '#7A6A62', fontSize: 12, margin: '4px 0 0', lineHeight: 1.45 },
  headline: { display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap', marginTop: 13 },
  figure: {
    display: 'inline-flex', alignItems: 'baseline', gap: 5, padding: '5px 11px', borderRadius: 999,
    background: '#fff', border: '1px solid #E2D6CB', color: '#6B5A52', fontSize: 12, fontWeight: 700,
  },
  number: { color: '#3E2723', fontSize: 15, fontWeight: 850 },
  // The disclosure that says the catalog may know another way to buy this.
  // Quiet by default — it is an offer, not an alert.
  pill: {
    border: '1.5px solid #D9CEC4', borderRadius: 999, background: '#fff', color: '#6B5A52',
    minHeight: 30, padding: '4px 11px', fontSize: 11.5, fontWeight: 750, cursor: 'pointer',
  },
  pillOpen: {
    border: '1.5px solid #5A4A42', borderRadius: 999, background: '#5A4A42', color: '#fff',
    minHeight: 30, padding: '4px 11px', fontSize: 11.5, fontWeight: 750, cursor: 'pointer',
  },
  altLink: {
    border: '1.5px solid #D9CEC4', borderRadius: 999, background: '#fff', color: '#6B5A52',
    minHeight: 34, padding: '6px 13px', fontSize: 12, fontWeight: 750, cursor: 'pointer', marginLeft: 'auto',
  },
  toolbar: { display: 'flex', alignItems: 'end', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' },
  searchWrap: { flex: '1 1 300px', maxWidth: 560 },
  label: {
    display: 'block', color: '#6B5A52', fontSize: 10, fontWeight: 800,
    textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4,
  },
  summary: { color: '#8B7B73', fontSize: 12, paddingBottom: 8 },
  list: { display: 'grid', gap: 11 },
  card: {
    border: '1px solid #E4D9D0', borderRadius: 13, background: '#fff', overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(62,39,35,.045)',
  },
  cardHead: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12,
    padding: '12px 13px 9px', flexWrap: 'wrap',
  },
  cardTitle: { color: '#3E2723', fontSize: 15, fontWeight: 800, margin: 0 },
  cardMeta: { color: '#8B7B73', fontSize: 11, marginTop: 2 },
  products: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
    gap: 8, padding: '0 13px 13px',
  },
  product: {
    display: 'grid', gridTemplateColumns: '28px minmax(0, 1fr)', alignItems: 'start',
    gap: 8, border: '1px solid #EEE5DF', borderRadius: 10, background: '#FBF8F5', padding: '8px 9px',
  },
  letter: {
    display: 'inline-flex', width: 25, height: 25, borderRadius: 7, alignItems: 'center',
    justifyContent: 'center', background: '#EDE4DC', color: '#5A4A42', fontSize: 10, fontWeight: 900,
  },
  vendorName: { color: '#3E2723', fontSize: 12.5, fontWeight: 750, lineHeight: 1.25 },
  productName: { color: '#7D6D64', fontSize: 10.5, marginTop: 3, lineHeight: 1.35 },
  price: { color: '#5A4A42', fontSize: 11, marginTop: 4 },
  // The winning tile: a very light positive wash and a slightly greener
  // border, nothing louder. It must read as "this one" at a glance without
  // turning the card into a traffic light (.builderrules keeps the palette
  // brown-and-grey; this is the one restrained exception, a tint not a fill).
  productWinner: {
    display: 'grid', gridTemplateColumns: '28px minmax(0, 1fr)', alignItems: 'start',
    gap: 8, border: '1px solid #C7DCCB', borderRadius: 10, background: '#F3F8F3', padding: '8px 9px',
  },
  letterWinner: {
    display: 'inline-flex', width: 25, height: 25, borderRadius: 7, alignItems: 'center',
    justifyContent: 'center', background: '#DCEBE0', color: '#22593E', fontSize: 10, fontWeight: 900,
  },
  // The purchasing result — the loudest badge on the tile.
  lowest: {
    display: 'inline-flex', marginLeft: 5, padding: '1px 6px', borderRadius: 999, background: '#DCEBE0',
    color: '#22593E', fontSize: 8.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.03em',
  },
  // Savings line: the number a buyer acts on, directly under the comparable.
  savings: {
    color: '#22593E', fontSize: 11, fontWeight: 750, marginTop: 3,
  },
  // Role badges (which item counts, which was bought last). Quieter than the
  // price result on purpose — they describe bookkeeping, not the best buy.
  countItem: {
    display: 'inline-flex', marginLeft: 5, padding: '1px 5px', borderRadius: 999, background: '#EDE4DC',
    color: '#5A4A42', fontSize: 8.5, fontWeight: 800, textTransform: 'uppercase',
  },
  lastUsed: {
    display: 'inline-flex', marginLeft: 5, padding: '1px 5px', borderRadius: 999,
    border: '1px solid #E0D6CC', background: '#FBF8F5',
    color: '#7D6D64', fontSize: 8.5, fontWeight: 800, textTransform: 'uppercase',
  },
  // No comparable price and an honest reason why — never a blank line.
  needsConfirm: {
    color: '#8A5A00', fontSize: 10.5, marginTop: 4, lineHeight: 1.35,
  },
  needsConfirmButton: {
    display: 'block', width: '100%', border: 0, padding: 0, background: 'transparent',
    textAlign: 'left', textDecoration: 'underline', textUnderlineOffset: 2,
    cursor: 'pointer', fontFamily: 'inherit',
  },
  // The two labelled money lines on a vendor tile. The label words are the
  // point: a purchase price describes the vendor's REAL pack, a comparable
  // price describes the group's one unit, and joining them on one line is how
  // the two got read as the same number.
  priceLabel: {
    color: '#8B7B73', fontSize: 9, fontWeight: 800, textTransform: 'uppercase',
    letterSpacing: '.04em', marginRight: 4,
  },
  // AI-estimated denominator. Deliberately the quietest mark on the tile: it
  // describes how the quantity was derived, NOT how good the price is, and an
  // estimated denominator stays fully eligible to win on price.
  estimated: {
    display: 'inline-flex', marginLeft: 5, padding: '0 4px', borderRadius: 999,
    border: '1px solid #E7DED2', background: 'transparent',
    color: '#9C8C80', fontSize: 8, fontWeight: 700, textTransform: 'uppercase',
  },
  empty: {
    border: '1px solid #E6DDD6', borderRadius: 12, background: '#FAF7F4',
    padding: '18px 16px', color: '#6F6259', fontSize: 12,
  },
};

// Plain-English for every machine reason assembleStoredComparison can give a
// vendor tile with no comparable price. A blank line is never an acceptable
// final state — the buyer is told what could not be worked out.
const UNRESOLVED_REASONS = {
  no_stored_comparison: 'this pack has not been measured against the group\u2019s unit yet.',
  no_stored_quantity: 'the pack size behind this price is not known yet.',
  unit_not_convertible: 'this pack is measured in a different dimension from the rest of the group.',
  package_basis_conflict: 'this pack\u2019s recorded facts disagree with each other.',
  fingerprint_mismatch: 'the pack changed since it was last measured.',
  same_unit_quantity_disagreement: 'the stored quantity disagrees with the pack on file.',
  converted_quantity_disagreement: 'the stored quantity disagrees with the pack on file.',
  no_row: 'this vendor product was not measured in this comparison.',
};

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const letterFor = (index) => (index < LETTERS.length
  ? LETTERS[index]
  : `${LETTERS[Math.floor(index / LETTERS.length) - 1]}${LETTERS[index % LETTERS.length]}`);

const money = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) && parsed > 0 ? `$${parsed.toFixed(2)}` : null;
};

const searchText = (group) => [
  group.canonicalName,
  group.category,
  group.countUnit,
  // memberName keeps a folded-away sibling findable by ITS name: the group is
  // one thing, and searching any member's name must surface its card.
  ...(group.products || []).flatMap((product) => [
    product.vendor, product.name, product.pack, product.memberName,
  ]),
].map(text).filter(Boolean).join(' ').toLowerCase();

// The card-level display precision. comparablePrecision picks the decimals
// that keep the winner visibly cheaper; on top of that, a sub-cent unit cost
// must never round to a bare "$0.00" — escalate (max 4, the same ceiling
// comparablePrecision uses) until every cost on the card prints as nonzero.
const cardDecimals = (costs) => {
  const { decimals: base, subCent } = comparablePrecision(costs);
  let decimals = base;
  while (
    decimals < 4
    && costs.some((cost) => Number.parseFloat(roundHalfUpFixed(cost, decimals)) === 0)
  ) {
    decimals += 1;
  }
  return { decimals, subCent };
};

export default function RelatedProductGroupsView({
  items = [],
  optionsByItemId = {},
  vendorNameById = {},
  comparisonItemById = {},
  // Confirmed item_relationships rows — the EQUIVALENT working set that folds
  // sibling items into one card. Defaults keep pre-link callers unchanged.
  itemRelationships = [],
  // equivalent_group_comparisons rows keyed by group_key (may be empty — the
  // stored canonical and the group denominator are used when present, never
  // required).
  groupComparisonByKey = {},
  // { [purchaseOptionId]: isoDate } — the latest SENT purchase order carrying
  // that option. Real purchasing evidence behind the "Last Used" badge; empty
  // simply means no order history is loaded and the badge stays off.
  orderUseByOptionId = {},
  // The store the group editor writes against, and the host's refetch. Both
  // optional: without them the card still renders, it simply cannot edit.
  storeId,
  onChanged,
  // Opens the per-option Vendor Options editor. Edit on a CARD now opens the
  // group editor (the card is a group, not an item); this stays as the "Edit
  // vendor option" affordance on a single vendor row inside it.
  onEditItem,
  onOpenSubstitutes,
  substituteCount = 0,
}) {
  const [query, setQuery] = useState('');
  // The group whose editor is open, keyed the way the fold keys groups
  // (equivalent group id, or the item's own id when it is in no group). Held
  // as a KEY rather than as the group object so a refetch re-renders the open
  // editor from fresh data — and so a group that stops qualifying as related
  // closes the editor instead of showing a stale card.
  const [editingGroupKey, setEditingGroupKey] = useState(null);
  // Which card's "possible related products" disclosure is open. One at a
  // time: the panel is a decision surface, not a second list to scan.
  const [openSiblingsKey, setOpenSiblingsKey] = useState(null);
  // Sort controls for the related products cards — the head (canonical) item
  // is the sort point, because a group pools vendors that may have different
  // categories; sorting by a pooled value would be ambiguous. The canonical is
  // the item identified as active for ordering elsewhere (stored canonical >
  // most recent operational use > lowest id via resolveCanonicalMemberId).
  const [relatedSortField, setRelatedSortField] = useState('name');
  const [relatedSortDirection, setRelatedSortDirection] = useState('asc');

  const workingSet = useMemo(
    () => buildWorkingSetGroups(itemRelationships),
    [itemRelationships],
  );
  const storedCanonicalByGroupKey = useMemo(() => {
    const map = {};
    Object.entries(groupComparisonByKey || {}).forEach(([groupKey, row]) => {
      if (row?.canonical_item_id !== null && row?.canonical_item_id !== undefined) {
        map[String(groupKey)] = String(row.canonical_item_id);
      }
    });
    return map;
  }, [groupComparisonByKey]);
  // The ONE denominator the group agreed on. Members' own comparison_unit
  // columns can disagree with each other (they were resolved per item before
  // the group existed); when the group layer has ruled, its unit wins for
  // every member so the card never compares two dimensions.
  const storedUnitByGroupKey = useMemo(() => {
    const map = {};
    Object.entries(groupComparisonByKey || {}).forEach(([groupKey, row]) => {
      if (text(row?.comparison_unit)) map[String(groupKey)] = text(row.comparison_unit);
    });
    return map;
  }, [groupComparisonByKey]);

  // Relationship endpoints are authoritative catalog members even when the
  // paginated inventory list has not reached them yet. Union the embedded
  // endpoint rows into the projection so a confirmed member (for example the
  // Costco Black Sip Straws item) cannot disappear merely because it lives on
  // a later page.
  const projectionItems = useMemo(() => {
    const byId = new Map();
    (Array.isArray(items) ? items : []).forEach((item) => {
      if (item?.id != null) byId.set(String(item.id), item);
    });
    (Array.isArray(itemRelationships) ? itemRelationships : []).forEach((row) => {
      for (const endpoint of [row?.item_a, row?.item_b]) {
        if (endpoint?.id == null || endpoint.is_active === false) continue;
        const key = String(endpoint.id);
        byId.set(key, { ...(byId.get(key) || {}), ...endpoint });
      }
    });
    return [...byId.values()];
  }, [items, itemRelationships]);

  // One card per confirmed EQUIVALENT group: the per-item projection folds so
  // "two limes items, one vendor product each" reads as one item with two
  // ways to buy it — which is what it is.
  const allGroups = useMemo(
    () => foldCatalogGroupsByEquivalence(
      buildRelatedProductCatalogGroups(projectionItems, optionsByItemId, vendorNameById),
      workingSet,
      { storedCanonicalByGroupKey, optionsByItemId },
    ),
    [projectionItems, optionsByItemId, vendorNameById, workingSet, storedCanonicalByGroupKey],
  );
  const metrics = useMemo(() => relatedProductCatalogMetrics(allGroups), [allGroups]);
  // Only items with more than one way to buy them. A single-product item is
  // not a relationship, and listing it next to itself buried the real ones.
  const related = useMemo(() => relatedProductCatalogGroupsOnly(allGroups), [allGroups]);

  // Per-unit comparison, per card. Read from the SAME view-model every other
  // surface renders from, so the number and the unit word can never come from
  // two places and disagree.
  //
  // The unit is the item's comparison_unit — NOT group.countUnit. countUnit is
  // `item.unit || 'each'`, which can never be falsy (so it is useless as an "is
  // the unit known?" test) and answers a different question: docs/multi-vendor-
  // purchasing.md gives apples counted in `each` but compared per pound.
  //
  // normalized_unit_cost is deliberately unread even though relatedProductCatalog
  // already carries it. Its unit is purchase_options.normalized_count_unit, which
  // this data path never fetches, and migration 20260814160200 sets that column to
  // 'pack' on live rows whose cost is package_price/6. A number from one basis
  // printed against a label from another is the failure this avoids.
  const comparisonByItemId = useMemo(() => {
    const map = new Map();
    const noteByItemId = new Map();
    // The group's ONE denominator word, kept alongside the per-tile facts so
    // the group editor states the same unit the tiles were priced in rather
    // than working one out for itself.
    const unitLabelByItemId = new Map();
    related.forEach((group) => {
      const item = {
        ...group.item,
        ...(comparisonItemById?.[group.itemId] || comparisonItemById?.[String(group.itemId)] || {}),
      };
      // A folded group's engine input is every MEMBER's active options — the
      // pool the card's tiles were drawn from. The group layer stamps members
      // with one uniform comparison_unit; when the canonical's is missing,
      // assembleStoredComparison's unanimous-snapshot inference still answers.
      const memberIds = group.memberItemIds?.length ? group.memberItemIds : [group.itemId];
      const seenOptionIds = new Set();
      const options = [];
      memberIds.forEach((memberId) => {
        (optionsByItemId?.[memberId] || [])
          .filter((option) => option && option.status !== 'inactive')
          .forEach((option) => {
            const key = option.id !== null && option.id !== undefined ? String(option.id) : null;
            if (key && seenOptionIds.has(key)) return;
            if (key) seenOptionIds.add(key);
            options.push(option);
          });
      });
      // Each pooled option's package basis belongs to ITS OWN item, not to
      // the canonical: the fingerprint stamped on a sibling's snapshot was
      // computed against that sibling's unit, so validating it against the
      // canonical's unit silently threw away a perfectly good comparison.
      const itemsById = {};
      memberIds.forEach((memberId) => {
        const base = comparisonItemById?.[memberId] || comparisonItemById?.[String(memberId)] || null;
        if (base) itemsById[String(memberId)] = base;
      });
      const groupKey = group.equivalentGroupId != null ? String(group.equivalentGroupId) : null;
      const display = buildComparablePriceDisplay({
        item,
        options,
        vendorNameById,
        itemsById,
        comparisonUnit: (groupKey && storedUnitByGroupKey[groupKey]) || null,
      });
      const unitLabel = text(display.comparisonUnitLabel);
      unitLabelByItemId.set(String(group.itemId), unitLabel);
      const byOptionId = new Map(display.options.map((row) => [String(row.optionId), row]));

      // Rank over the products actually ON SCREEN. display.options is every
      // active option; group.products is deduped (same vendor + same SKU renders
      // once). Ranking the engine's list would let a collapsed duplicate read as
      // a tie against a row nobody can see.
      const visible = group.products.map((product) => ({
        product, row: byOptionId.get(String(product.id)) || null,
      }));
      const costs = visible
        .map(({ row }) => row?.comparableUnitCost)
        .filter((cost) => Number.isFinite(cost) && cost > 0);
      const { decimals, subCent } = cardDecimals(costs);

      // A vendor with a price but no comparable cost cannot be ranked, so
      // nothing on this card may claim to be lowest — that vendor might be the
      // real winner. Mirrors ManageRelationshipsView's comparisonBlocked gate.
      const blocked = visible.some(({ product, row }) => (
        product.packagePrice != null
        && !(row && Number.isFinite(row.comparableUnitCost) && row.comparableUnitCost > 0)
      ));
      const lowest = costs.length ? Math.min(...costs) : null;
      const lowestIds = new Set();
      if (unitLabel && !blocked && !subCent && costs.length > 1) {
        visible.forEach(({ product, row }) => {
          if (row && row.comparableUnitCost === lowest) lowestIds.add(String(product.id));
        });
      }
      if (subCent && unitLabel && costs.length > 1) {
        // Same wording as buildComparablePriceDisplay.subCentNote — the note
        // replaces a misleading "Lowest" tag when the spread is sub-cent.
        noteByItemId.set(String(group.itemId), `less than 1¢/${unitLabel} difference`);
      }

      // The spread this card is worth, in the group's own denominator. Uses
      // the same `blocked` gate as the Lowest tag: if some priced vendor could
      // not be compared, no saving may be quoted against a "highest" that
      // might not be the highest.
      const savings = buildGroupSavings(
        visible.map(({ product, row }) => ({
          optionId: String(product.id),
          comparableUnitCost: row?.comparableUnitCost ?? null,
        })),
        { blocked: blocked || !unitLabel },
      );
      const savingsLine = formatSavingsLine(savings, unitLabel);
      // At most one per group, and only on real purchasing evidence.
      const lastUsed = resolveLastUsedOption(options, { orderUseByOptionId });

      const entries = new Map();
      visible.forEach(({ product, row }) => {
        const cost = row?.comparableUnitCost;
        const priced = unitLabel && Number.isFinite(cost) && cost > 0;
        const isLowest = lowestIds.has(String(product.id));
        entries.set(String(product.id), {
          rawComparableCost: Number.isFinite(cost) && cost > 0 ? cost : null,
          // No proven unit word, no per-unit price: formatComparablePrice emits
          // a bare "$0.04" when the label is falsy, and an unlabelled per-unit
          // figure on a price line is worse than no figure at all.
          comparable: priced ? formatComparablePrice(cost, unitLabel, { decimals }) : null,
          estimated: row?.estimated === true,
          isLowest,
          isTied: lowestIds.size > 1 && isLowest,
          // The savings line belongs to the winner only — it is the reason to
          // pick that tile, and repeating it on every tile would be noise.
          savingsLine: isLowest && !savings.tied ? savingsLine : null,
          isLastUsed: lastUsed != null && String(lastUsed.optionId) === String(product.id),
          lastUsedTitle: lastUsed != null && String(lastUsed.optionId) === String(product.id)
            ? lastUsedTitle(lastUsed)
            : null,
          // A tile with no comparable price says WHY, never nothing.
          unresolvedReason: priced ? null : (row?.unresolvedReason ?? 'no_stored_comparison'),
        });
      });
      map.set(String(group.itemId), entries);
    });
    return { entriesByItemId: map, noteByItemId, unitLabelByItemId };
  }, [
    related, optionsByItemId, vendorNameById, comparisonItemById,
    storedUnitByGroupKey, orderUseByOptionId,
  ]);

  // Helpers for sort — vendor/price use the HEAD (canonical) product.
  // That item is the group's representative for ordering/counting
  // (see resolveCanonicalMemberId), so its vendor/category/price define the
  // group's position when members differ.
  const canonicalProductForSort = (group) => {
    const products = group?.products || [];
    const found = products.find((p) => p.isCanonical);
    return found || products[0] || null;
  };
  const vendorForSort = (group) => text(canonicalProductForSort(group)?.vendor).toLowerCase();
  const priceForSort = (group) => {
    const canonical = canonicalProductForSort(group);
    // Prefer comparable unit cost of the head product when available — that is
    // the purchasing-relevant price — otherwise fall back to its package price.
    const entries = comparisonByItemId?.entriesByItemId?.get(String(group.itemId));
    const entry = canonical ? entries?.get(String(canonical.id)) : null;
    // entries store formatted comparable; retrieve raw cost via canonical product's own price as fallback
    // raw comparable cost is available as entry?.rawComparableCost if comparisonByItemId populates it
    const rawComparable = entry?.rawComparableCost;
    if (Number.isFinite(rawComparable) && rawComparable > 0) return rawComparable;
    const fallback = canonical?.packagePrice ?? canonical?.normalizedUnitCost ?? null;
    const parsed = fallback != null ? Number.parseFloat(fallback) : Number.NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  };

  const visible = useMemo(() => {
    const normalized = text(query).toLowerCase();
    let result = normalized ? related.filter((group) => searchText(group).includes(normalized)) : [...related];
    const direction = relatedSortDirection === 'asc' ? 1 : -1;
    const compare = (left, right) => {
      let leftValue;
      let rightValue;
      let numeric = false;
      if (relatedSortField === 'vendor') {
        leftValue = vendorForSort(left);
        rightValue = vendorForSort(right);
      } else if (relatedSortField === 'category') {
        leftValue = text(left.category).toLowerCase();
        rightValue = text(right.category).toLowerCase();
      } else if (relatedSortField === 'price') {
        numeric = true;
        leftValue = priceForSort(left);
        rightValue = priceForSort(right);
      } else {
        leftValue = text(left.canonicalName).toLowerCase();
        rightValue = text(right.canonicalName).toLowerCase();
      }
      // Blanks last regardless of direction
      const leftBlank = leftValue === null || leftValue === undefined || leftValue === '';
      const rightBlank = rightValue === null || rightValue === undefined || rightValue === '';
      if (leftBlank !== rightBlank) return leftBlank ? 1 : -1;
      if (leftBlank) return 0;
      let cmp = 0;
      if (numeric) {
        cmp = Number(leftValue) - Number(rightValue);
      } else {
        cmp = String(leftValue).localeCompare(String(rightValue), undefined, { sensitivity: 'base', numeric: true });
      }
      return direction * cmp;
    };
    if (['name', 'vendor', 'category', 'price'].includes(relatedSortField)) {
      result = [...result].sort(compare);
    }
    return result;
  }, [query, related, relatedSortField, relatedSortDirection, comparisonByItemId]);

  // Every catalog row the projection knows about, so the editor can name the
  // member item behind a vendor row and hand it to the Vendor Options editor.
  const itemById = useMemo(() => {
    const map = new Map();
    projectionItems.forEach((item) => {
      if (item?.id != null) map.set(String(item.id), item);
    });
    return map;
  }, [projectionItems]);

  // What the catalog already suspects might be another way to buy each group,
  // computed once for every card rather than per render of the panel.
  const siblingsByGroupKey = useMemo(() => {
    const map = new Map();
    related.forEach((group) => {
      const memberIds = group.memberItemIds?.length ? group.memberItemIds : [group.itemId];
      const siblings = potentialSiblingsForGroup(itemRelationships, memberIds);
      if (siblings.length) map.set(String(group.equivalentGroupId ?? group.itemId), siblings);
    });
    return map;
  }, [related, itemRelationships]);

  const groupKeyOf = (group) => String(group.equivalentGroupId ?? group.itemId);
  // Looked up fresh on every render: after the editor's own onChanged refetch
  // this is the recomputed group, and when the group stops having a second way
  // to buy it is simply gone and the editor closes.
  const editingGroup = editingGroupKey === null
    ? null
    : related.find((group) => groupKeyOf(group) === editingGroupKey) || null;

  return (
    <div style={styles.shell} data-testid="related-products-workspace">
      <div style={styles.intro}>
        <h3 style={styles.title}>Related products</h3>
        <p style={styles.hint}>
          Items you can buy more than one way. Each card is a single inventory item you count
          once, with every vendor product that fills it — the same grouping the catalog shows.
        </p>
        <div style={styles.headline}>
          <span style={styles.figure}>
            <span style={styles.number}>{metrics.relatedGroupCount}</span>
            item{metrics.relatedGroupCount === 1 ? '' : 's'} with more than one vendor
          </span>
          <span style={styles.figure}>
            <span style={styles.number}>{metrics.relatedProductCount}</span>
            vendor product{metrics.relatedProductCount === 1 ? '' : 's'} across them
          </span>
          {typeof onOpenSubstitutes === 'function' && (
            <button type="button" style={styles.altLink} data-testid="open-substitutes" onClick={onOpenSubstitutes}>
              Substitutes &amp; alternatives{substituteCount > 0 ? ` (${substituteCount})` : ''} →
            </button>
          )}
        </div>
      </div>

      <div>
        <div style={styles.toolbar}>
          <div style={styles.searchWrap}>
            <label htmlFor="related-products-search" style={styles.label}>Search related products</label>
            <input
              id="related-products-search"
              type="search"
              className="form-control"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search item name, vendor, pack, category…"
            />
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'end', flexWrap: 'wrap' }}>
            <div>
              <label htmlFor="related-sort-by" style={styles.label}>Sort by</label>
              <select
                id="related-sort-by"
                className="form-select form-select-sm"
                value={relatedSortField}
                onChange={(event) => setRelatedSortField(event.target.value)}
                data-testid="related-sort-by"
                style={{ minWidth: 130 }}
              >
                <option value="name">Item name</option>
                <option value="vendor">Vendor</option>
                <option value="category">Category</option>
                <option value="price">Price</option>
              </select>
            </div>
            <div>
              <label htmlFor="related-sort-direction" style={styles.label}>Order</label>
              <select
                id="related-sort-direction"
                className="form-select form-select-sm"
                value={relatedSortDirection}
                onChange={(event) => setRelatedSortDirection(event.target.value)}
                data-testid="related-sort-direction"
                style={{ minWidth: 110 }}
              >
                <option value="asc">Ascending</option>
                <option value="desc">Descending</option>
              </select>
            </div>
          </div>
          <div style={styles.summary}>
            {visible.length} of {related.length} item{related.length === 1 ? '' : 's'}
          </div>
        </div>
        <div style={{ height: 10 }} />

        {!related.length ? (
          <div style={styles.empty}>
            <strong>No items have a second way to buy them yet.</strong>
            <div className="mt-1">
              Import a price list or invoice from another vendor, or open an item and add a vendor
              product to it. Items with one vendor are not listed here.
            </div>
          </div>
        ) : !visible.length ? (
          <div style={styles.empty}>No related products match that search.</div>
        ) : (
          <div style={styles.list}>
            {visible.map((group) => {
              const subCentNote = comparisonByItemId.noteByItemId.get(String(group.itemId)) || null;
              const groupComparisonUnit = comparisonByItemId.unitLabelByItemId.get(String(group.itemId)) || '';
              return (
              <article key={group.itemId} style={styles.card} data-testid="related-product-card">
                <div style={styles.cardHead}>
                  <div>
                    <h4 style={styles.cardTitle}>{group.canonicalName}</h4>
                    <div style={styles.cardMeta}>
                      {group.products.length} ways to buy it
                      {(group.memberItemIds?.length ?? 0) > 1
                        ? ` across ${group.memberItemIds.length} linked items` : ''}
                      {' · counted in '}{group.countUnit}
                      {group.category ? ` · ${group.category}` : ''}
                      {subCentNote ? ` · ${subCentNote}` : ''}
                    </div>
                    {group.missingMemberItemIds?.length > 0 && (
                      <div style={styles.needsConfirm} data-testid="related-product-missing-members">
                        {group.missingMemberItemIds.length} confirmed Equivalent member
                        {group.missingMemberItemIds.length === 1 ? '' : 's'} could not be loaded.
                        The group is shown incomplete instead of silently hiding the member.
                      </div>
                    )}
                  </div>
                  {/* Edit edits the GROUP on screen. It used to open the
                      per-item Vendor Options modal for group.item, which is
                      one member of a card that can span several linked items
                      and several vendors — the editor for one purchasing
                      option, opened from a control that promised the group.
                      Editing a single vendor option is still one click away,
                      from that vendor's own row inside the group editor. */}
                  <div style={{ display: 'flex', gap: 7, alignItems: 'center', flexWrap: 'wrap' }}>
                    {/* The catalog often already suspects an N+1 way to buy
                        this. That evidence used to be readable only in the
                        substitutes workspace; the pill brings the decision to
                        the group it is about. */}
                    {siblingsByGroupKey.has(groupKeyOf(group)) && (
                      <button
                        type="button"
                        style={openSiblingsKey === groupKeyOf(group) ? styles.pillOpen : styles.pill}
                        data-testid="potential-siblings-pill"
                        aria-expanded={openSiblingsKey === groupKeyOf(group)}
                        onClick={() => setOpenSiblingsKey(
                          (current) => (current === groupKeyOf(group) ? null : groupKeyOf(group)),
                        )}
                      >
                        {openSiblingsKey === groupKeyOf(group) ? 'Hide' : 'See'} possible related
                        product{siblingsByGroupKey.get(groupKeyOf(group)).length === 1 ? '' : 's'}
                        {' '}({siblingsByGroupKey.get(groupKeyOf(group)).length})
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      data-testid="related-product-edit"
                      onClick={() => setEditingGroupKey(groupKeyOf(group))}
                    >
                      Edit
                    </button>
                  </div>
                </div>
                <div style={styles.products} aria-label={`Vendor products for ${group.canonicalName}`}>
                  {group.products.map((product, index) => {
                    const facts = comparisonByItemId.entriesByItemId
                      .get(String(group.itemId))?.get(String(product.id)) || null;
                    const memberItemId = product.memberItemId ?? group.itemId;
                    const memberItem = itemById.get(String(memberItemId)) || null;
                    const winner = facts?.isLowest === true;
                    return (
                    <div
                      key={product.id || `${group.itemId}-${index}`}
                      style={winner ? styles.productWinner : styles.product}
                      data-testid="related-product-vendor"
                      data-winner={winner ? 'true' : undefined}
                    >
                      <span style={winner ? styles.letterWinner : styles.letter} aria-hidden="true">
                        {letterFor(index)}
                      </span>
                      <span style={{ minWidth: 0 }}>
                        {/* Badge order is the reading order the owner asked
                            for: the purchasing RESULT first (Lowest Price),
                            then the bookkeeping roles (which item counts,
                            which package was actually bought last). */}
                        <span style={styles.vendorName}>
                          {text(product.vendor) || 'Vendor not set'}
                          {facts?.isLowest && (
                            <span style={styles.lowest} data-testid="related-product-lowest">
                              {facts.isTied ? 'Tied lowest' : 'Lowest price'}
                            </span>
                          )}
                          {product.isCanonical && (
                            <span
                              style={styles.countItem}
                              data-testid="related-product-count-item"
                              title="The item that represents this group in the catalog, count sessions and pars"
                            >
                              Count item
                            </span>
                          )}
                          {facts?.isLastUsed && (
                            <span
                              style={styles.lastUsed}
                              data-testid="related-product-last-used"
                              title={facts.lastUsedTitle || 'The way this was bought most recently'}
                            >
                              Last used
                            </span>
                          )}
                        </span>
                        {text(product.name) && text(product.name) !== group.canonicalName && (
                          <div style={styles.productName}>{product.name}</div>
                        )}
                        {/* Two labelled lines (owner's rule 9). The Purchase
                            line states the vendor's REAL pack verbatim — a
                            "1000 ct box" is never relabelled "case" — and the
                            Comparable line states the group's one unit. */}
                        <div style={styles.price} data-testid="related-product-purchase">
                          <span style={styles.priceLabel}>Purchase</span>
                          {[text(product.pack) || null, money(product.packagePrice)]
                            .filter(Boolean).join(' — ') || 'No price on file'}
                        </div>
                        {facts?.comparable ? (
                          <div style={styles.price} data-testid="related-product-comparable">
                            <span style={styles.priceLabel}>Comparable</span>
                            {facts.estimated ? `~${facts.comparable}` : facts.comparable}
                            {facts.estimated && (
                              <span
                                style={styles.estimated}
                                data-testid="related-product-estimated"
                                title="The pack quantity behind this figure was estimated by AI. It describes how the number was derived — not how good the price is."
                              >
                                Est
                              </span>
                            )}
                          </div>
                        ) : typeof onEditItem === 'function' && memberItem ? (
                          /* Two states only, never a blank: a comparable
                             price, or the reason there is not one yet. The
                             unresolved state is also the shortest route to
                             the existing human comparison editor. */
                          <button
                            type="button"
                            style={{ ...styles.needsConfirm, ...styles.needsConfirmButton }}
                            data-testid="related-product-needs-confirmation"
                            onClick={() => onEditItem(memberItem, {
                              reviewComparison: true,
                              comparisonUnit: groupComparisonUnit,
                            })}
                            aria-label={`Review comparison quantity for ${text(product.vendor) || product.name || group.canonicalName}`}
                          >
                            Needs quantity confirmation — {UNRESOLVED_REASONS[facts?.unresolvedReason]
                              || UNRESOLVED_REASONS.no_stored_comparison}
                          </button>
                        ) : (
                          <div style={styles.needsConfirm} data-testid="related-product-needs-confirmation">
                            Needs quantity confirmation — {UNRESOLVED_REASONS[facts?.unresolvedReason]
                              || UNRESOLVED_REASONS.no_stored_comparison}
                          </div>
                        )}
                        {facts?.savingsLine && (
                          <div style={styles.savings} data-testid="related-product-savings">
                            {facts.savingsLine}
                          </div>
                        )}
                      </span>
                    </div>
                    );
                  })}
                </div>
                {openSiblingsKey === groupKeyOf(group) && (
                  <PotentialSiblingsPanel
                    group={group}
                    siblings={siblingsByGroupKey.get(groupKeyOf(group)) || []}
                    itemById={itemById}
                    optionsByItemId={optionsByItemId}
                    vendorNameById={vendorNameById}
                    storeId={storeId}
                    onChanged={onChanged}
                  />
                )}
              </article>
              );
            })}
          </div>
        )}
      </div>

      {editingGroup && createPortal(
        <div
          className="import-tool-modal-overlay"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setEditingGroupKey(null);
          }}
        >
          <div
            className="import-tool-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`Edit related products for ${editingGroup.canonicalName}`}
            onMouseDown={(event) => event.stopPropagation()}
            style={{ maxWidth: 760, width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
          >
            <div className="import-tool-modal-header">
              <div>
                <h6 style={{ margin: 0, color: '#3a2e28' }}>Related products</h6>
                <div style={{ fontSize: '0.8rem', color: '#6f6259', marginTop: '0.15rem' }}>
                  Add or remove the ways you buy this one product
                </div>
              </div>
              <button
                type="button"
                className="import-tool-modal-close"
                onClick={() => setEditingGroupKey(null)}
                aria-label="Close"
              >×</button>
            </div>
            <div className="import-tool-modal-body" style={{ overflowY: 'auto', flex: 1, padding: '1rem' }}>
              <RelatedProductGroupEditor
                group={editingGroup}
                factsByOptionId={comparisonByItemId.entriesByItemId.get(String(editingGroup.itemId)) || null}
                comparisonUnitLabel={comparisonByItemId.unitLabelByItemId.get(String(editingGroup.itemId)) || ''}
                itemRelationships={itemRelationships}
                items={projectionItems}
                optionsByItemId={optionsByItemId}
                vendorNameById={vendorNameById}
                itemById={itemById}
                storeId={storeId}
                onChanged={onChanged}
                onEditItem={typeof onEditItem === 'function'
                  ? (memberItem, options) => {
                    setEditingGroupKey(null);
                    onEditItem(memberItem, options);
                  }
                  : undefined}
                onClose={() => setEditingGroupKey(null)}
              />
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
